import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../api/admin/adminApi';
import '../../styles/AdminDashboard.css';

const EMPTY_FORM = {
  enterpriseId: '',
  kind: 'member_capacity',
  quantity: 1,
  expiresAt: '',
  campaign: '',
  reason: '',
};

export default function AdminPromotionsPage() {
  const { registerShellMeta, setRefreshHandler } = useOutletContext() || {};
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  const resetNotice = useCallback(() => {
    setError('');
    setNotice('');
  }, []);

  useEffect(() => {
    registerShellMeta?.({
      title: 'Promotions',
      subtitle: 'Campaign capacity that expires without changing the plan',
    });
    setRefreshHandler?.(resetNotice);
  }, [registerShellMeta, setRefreshHandler, resetNotice]);

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await adminApi.grantPromotion({
        enterpriseId: form.enterpriseId.trim(),
        kind: form.kind,
        quantity: Number(form.quantity),
        startsAt: new Date().toISOString(),
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : undefined,
        campaign: form.campaign.trim(),
        reason: form.reason.trim(),
      });
      setNotice('Promotion recorded. Extra capacity is attached to the existing entitlement and will expire; the company plan is unchanged.');
      setForm(EMPTY_FORM);
      if (res?.capacity?.overAllocated) {
        setNotice((prev) => `${prev} This workspace is currently over-allocated.`);
      }
    } catch (err) {
      setError(err.message || 'Could not grant promotion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="analytics-section">
        <div className="admin-table-header">
          <div>
            <h2>Promotional capacity</h2>
            <p className="admin-table-subtitle">
              This adds dated extra member capacity or Pro licences on top of the company&apos;s current entitlement.
              It is not a new plan, not a billing change, and it expires.
            </p>
          </div>
        </div>

        {error ? <p className="error-message" role="alert">{error}</p> : null}
        {notice ? <p className="admin-table-subtitle" role="status">{notice}</p> : null}

        <form className="admin-inperson-distribution-form" onSubmit={submit}>
          <label>
            Company id
            <input
              value={form.enterpriseId}
              onChange={setField('enterpriseId')}
              required
              placeholder="Enterprise ObjectId"
            />
          </label>
          <label>
            Kind
            <select value={form.kind} onChange={setField('kind')}>
              <option value="member_capacity">Member capacity</option>
              <option value="pro_licence">Pro licence</option>
            </select>
          </label>
          <label>
            Quantity
            <input
              type="number"
              min="1"
              step="1"
              value={form.quantity}
              onChange={setField('quantity')}
              required
            />
          </label>
          <label>
            Expires
            <input
              type="datetime-local"
              value={form.expiresAt}
              onChange={setField('expiresAt')}
            />
          </label>
          <label>
            Campaign
            <input
              value={form.campaign}
              onChange={setField('campaign')}
              placeholder="Launch, partner trial…"
            />
          </label>
          <label>
            Reason
            <input
              value={form.reason}
              onChange={setField('reason')}
              placeholder="Why this extra capacity exists"
            />
          </label>
          <button type="submit" className="btn" disabled={saving}>
            {saving ? 'Granting…' : 'Grant promotion'}
          </button>
        </form>
      </div>
    </div>
  );
}
